import React, { useContext, useEffect, useState } from 'react'
import { AuthContext } from '../../../context/AuthContext'
import { useI18n } from '../../../i18n/I18nContext'
import { isWorkerDocumentFileUrl, loadWorkerDocumentFile } from '../../../services/workerDocumentFile'

function useWorkerFile(source: string, ownerId: string) {
  const auth = useContext(AuthContext)
  const active = auth?.user?.id === ownerId && !auth.loading
  const key = `${ownerId}:${source}`
  const [state, setState] = useState<{ key: string; url?: string; filename?: string; failed?: boolean } | null>(null)
  useEffect(() => {
    setState(null)
    if (!active || !isWorkerDocumentFileUrl(source)) return
    const controller = new AbortController()
    let objectUrl: string | undefined
    void loadWorkerDocumentFile(source, ownerId, controller.signal).then(({ blob, filename }) => {
      if (controller.signal.aborted) return
      objectUrl = URL.createObjectURL(blob)
      setState({ key, url: objectUrl, filename })
    }).catch(() => { if (!controller.signal.aborted) setState({ key, failed: true }) })
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl) }
  }, [source, ownerId, active, key])
  return active && state?.key === key ? state : null
}

export function WorkerDocumentFileLink({ source, ownerId, className }: { source: string; ownerId: string; className?: string }) {
  const { t } = useI18n()
  const file = useWorkerFile(source, ownerId)
  if (file?.failed) return <span role="alert">{t('Unable to open document. Refresh and try again.')}</span>
  return file?.url ? <><a className={className} href={file.url} target="_blank" rel="noopener noreferrer">{t('Open')}</a> <a className={className} href={file.url} download={file.filename}>{t('Download')}</a></>
    : <span role="status">{t('Loading document…')}</span>
}

export function WorkerDocumentImage({ source, ownerId, ...props }: { source: string; ownerId: string } & Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'>) {
  const file = useWorkerFile(source, ownerId)
  // Public/external avatars retain their existing URL and never receive bearer headers.
  const url = isWorkerDocumentFileUrl(source) ? file?.url : source
  return url ? <img {...props} src={url} /> : null
}
